// ============================================================
// WorkspaceCommandShell — the shared frame for every major
// Square Code workspace tab (QUAM conversion).
//
//   ┌─ plane: near-black canvas + technical grid + accent glow ─┐
//   │ KICKER · identity line          status chips · actions    │
//   │ Title (editorial)                                          │
//   │ subtitle                                                   │
//   │ [ mode tabs ]                                              │
//   ├──────────────────────────────────┬────────────────────────┤
//   │ main content (children)          │ right rail (optional)  │
//   ├──────────────────────────────────┴────────────────────────┤
//   │ composer / primary action footer (optional)                │
//   └────────────────────────────────────────────────────────────┘
//
// Boundary: this component renders layout only. It performs no
// fetch and no provider action — powered/blocked truth is the
// caller's responsibility, surfaced through `status` chips.
// The shell's left nav and the OLIVIUM right panel (detail /
// comms / action) stay untouched: this rail lives INSIDE the
// tab, it is not a fourth panel mode.
// ============================================================

import WorkspaceModeTabs from './WorkspaceModeTabs'
import WorkspaceStatusChip from './WorkspaceStatusChip'
import {
  tint,
  WORKSPACE_ACCENTS,
  WORKSPACE_FONT,
  WORKSPACE_GRID_URL,
  WORKSPACE_SURFACE,
  WORKSPACE_TEXT,
} from './workspaceTheme'

export default function WorkspaceCommandShell({
  kicker = 'SQUARE CODE · COMMAND CENTER',
  title,
  subtitle,
  accent = WORKSPACE_ACCENTS.cyan,
  status,            // string | string[] — resolved through the status vocabulary
  chips,             // extra ReactNode rendered beside status chips
  actions,           // ReactNode — header action buttons
  modes,             // [{ key, label, glyph, accent, badge, disabled }]
  activeMode,
  onModeChange,
  rail,              // ReactNode — right intelligence/notification rail
  railWidth = 316,
  composer,          // ReactNode — bottom command/action footer
  minHeight = 'calc(100vh - 240px)',
  children,
  style: styleOverride,
}) {
  const statusList = status == null ? [] : Array.isArray(status) ? status : [status]

  return (
    <section
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        minHeight,
        borderRadius: 'var(--sc-radius-xl, 16px)',
        border: `1px solid ${WORKSPACE_SURFACE.planeBorder}`,
        background: WORKSPACE_SURFACE.canvas,
        boxShadow: 'var(--sc-panel-shadow, 0 4px 24px rgba(0,0,0,0.4))',
        overflow: 'hidden',
        ...styleOverride,
      }}
    >
      {/* Technical grid — the command-center substrate */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage: WORKSPACE_GRID_URL,
          backgroundSize: '50px 50px',
        }}
      />
      {/* Single-accent ambient glow */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `radial-gradient(ellipse at 16% 0%, ${tint(accent, 7)} 0%, transparent 55%)`,
        }}
      />

      {/* ── Command header ── */}
      <header style={{ position: 'relative', padding: '20px 24px 0', flexShrink: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 220 }}>
            <div
              style={{
                fontSize: 10,
                letterSpacing: '0.32em',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: tint(accent, 75),
                fontFamily: WORKSPACE_FONT.mono,
              }}
            >
              {kicker}
            </div>
            {title && (
              <h1
                style={{
                  margin: '6px 0 0',
                  fontSize: 28,
                  fontWeight: 200,
                  letterSpacing: '-0.02em',
                  color: WORKSPACE_TEXT.primary,
                  fontFamily: WORKSPACE_FONT.ui,
                }}
              >
                {title}
              </h1>
            )}
            {subtitle && (
              <div
                style={{
                  marginTop: 4,
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: WORKSPACE_TEXT.tertiary,
                  fontFamily: WORKSPACE_FONT.ui,
                  maxWidth: 640,
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              paddingTop: 2,
            }}
          >
            {statusList.map((s, i) => (
              <WorkspaceStatusChip key={`${String(s)}-${i}`} status={s} />
            ))}
            {chips}
            {actions}
          </div>
        </div>

        {modes && modes.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <WorkspaceModeTabs modes={modes} active={activeMode} onChange={onModeChange} />
          </div>
        )}
        <div
          aria-hidden
          style={{ height: 1, background: WORKSPACE_SURFACE.line, marginTop: modes?.length ? 12 : 18 }}
        />
      </header>

      {/* ── Body: main pane + optional right rail ── */}
      <div
        style={{
          position: 'relative',
          flex: 1,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'stretch',
          minHeight: 0,
        }}
      >
        <main
          style={{
            flex: '2 1 460px',
            minWidth: 0,
            padding: '18px 24px 24px',
          }}
        >
          {children}
        </main>
        {rail && (
          <aside
            style={{
              flex: `1 1 280px`,
              maxWidth: railWidth,
              borderLeft: `1px solid ${WORKSPACE_SURFACE.lineFaint}`,
              background: 'rgba(255,255,255,0.008)',
              padding: '18px 18px 24px',
              minWidth: 0,
            }}
          >
            {rail}
          </aside>
        )}
      </div>

      {/* ── Composer / primary action footer ── */}
      {composer && (
        <footer
          style={{
            position: 'relative',
            flexShrink: 0,
            borderTop: `1px solid ${tint(accent, 10)}`,
            background: 'rgba(4, 2, 9, 0.6)',
            backdropFilter: 'blur(16px)',
            padding: '12px 24px 14px',
          }}
        >
          {composer}
        </footer>
      )}
    </section>
  )
}
